import { Box, Button, CircularProgress, Divider, FormHelperText, Paper, Typography } from '@mui/material';
import { Form, Formik, FormikConfig } from 'formik';
import React from 'react';
import Details from '../../../components/Details/Details';
import FormController from '../../../components/form_v2/highLevelComponents/FormController';
import FormActionButtonLayout from '../../../components/layout/Form/FormActionButtonLayout';
import FormLayout from '../../../components/layout/Form/FormLayout';
import FilterComponentV2 from '../../../components/TablegridWithFilters/FilterComponent/FilterComponentv2';
import TabsLayout from '../../../components/Tabs/TabsLayout';
import { createParavolo, createParavoloShort } from '../../../lib/fetchers';
import { eliminateOjectNotTruthyValues, isSubmitEnabled, matchRefsWithData } from '../../../lib/helperFuncs';
import { createParavoloRefs, createParavoloShortRefs } from '../../../lib/static/details/staticDetails';
import { createParavoloFields, createParavoloShortFields } from '../../../lib/static/filters/filterFields';
import { createParavoloInitialValues, createParavoloShortInitialValues } from '../../../lib/static/filters/initialValues';
import { createParavoloShortValidationSchema, createParavoloValidationSchema } from '../../../lib/static/filters/ValidationSchema';
import Theme from '../../../Themes/main.theme';

const CreateParavolo = () => {
  const [tab, setTab] = React.useState(0);
  const [data, setData] = React.useState();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  const onReset = (formik: any) => {   
    Object.keys(formik.values).map((value) => {
      formik.setFieldValue(value, createParavoloInitialValues[value]);
    });
  };

  const tabLabels = [
    {
      label: <Typography variant='h4'>Δημιουργία Παραβόλου</Typography>,
    },
    {
      label: <Typography variant='h4'>Δημιουργία Παραβόλου με ελάχιστα στοιχεία</Typography>,
    }
  ];

  const onSubmit = async (values: any) => {    
    setIsLoading(true);
    try {
      const response = await createParavolo(eliminateOjectNotTruthyValues(values));
      setError('');        
      setData({...response.data, ...response.data.paravoloStatus});
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };
  
  const onSubmitShort = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await createParavoloShort(eliminateOjectNotTruthyValues(values));
      setError('');
      setData({...response.data, ...response.data.paravoloStatus});
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const details = matchRefsWithData(data, createParavoloRefs);
  const details_short = matchRefsWithData(data, createParavoloShortRefs);

  const tab1 = <Box my={4}>
    <FormHelperText sx={{mb: 1}}>
      <Typography variant='subtitle1'>
        <ul>
          <li>
            Δημιουργία νέου παραβόλου με βάση τα στοιχεία του πολίτη.
          </li>
          <li>
            Το <span style={{fontWeight: 'bold'}}>ποσό που αποστέλλεται</span> είναι είτε το ποσό που είχε επιστραφεί από την 
            κλήση για την <span style={{fontWeight: 'bold'}}>ανάκτηση των τύπων παραβόλου</span>, είτε
            <span style={{fontWeight: 'bold'}}> το μεταβλητό ποσό</span> που έχει επιλέξει ο συναλλασσόμενος
          </li>
        </ul>
      </Typography>
    </FormHelperText>
    <Paper sx={{padding: 2}} elevation={3}>
      <Formik
        enableReinitialize
        onSubmit={() => undefined}
        initialValues={createParavoloInitialValues}
        validationSchema={createParavoloValidationSchema}
        validateOnBlur={true}
        validateOnMount
      >
        {formik =>
          (<div>
            <Form>
              <FormLayout>
                {createParavoloFields.map((el: any) => {
                  if (Array.isArray(el)) {
                    return <Box display='flex'
                      gap={3}
                      key={el + 'Box'}>
                      {el.map((subEl) => {
                        return (
                          <div style={{width: '100%'}} 
                            key={subEl.name}
                          >
                            <FormController 
                              {...subEl}
                              formik={formik}
                            />
                          </div>
                        );
                      })}
                    </Box>;
                  }
                  return (
                    <FormController 
                      key={el.name} 
                      formik={formik}
                      {...el}
                    />); 
                })}
              </FormLayout>
              <FormActionButtonLayout>
                <Button variant='outlined' color='primary'
                  onClick={() => onReset(formik)}
                  sx={{ borderRadius: 24, maxWidth: '150px', maxHeight: '50px', minWidth: '150px', minHeight: '50px'}}
                > Καθαρισμός </Button>
                <Button variant='contained'
                  disabled={isSubmitEnabled(formik?.errors, formik?.dirty)}
                  onClick={() => onSubmit(formik.values)}
                  sx={{ borderRadius: 24, maxWidth: '150px', maxHeight: '50px', minWidth: '150px', minHeight: '50px'}}
                > Δημιουργία</Button>
              </FormActionButtonLayout>
            </Form>
          </div>)
        }
      </Formik>
    </Paper>
    <Box mt={5} >
      {isLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
        : error
          ? <Typography variant='h4' display='flex'
            justifyContent='center'>
            {error}
          </Typography> 
          : data
            ? <Box height='100%'>
              <Details rows={details}/>
            </Box> 
            : null 
      }
    </Box>
  </Box>;

  const tab2 = <Box my={4}>
    <FormHelperText sx={{mb: 1}}>
      <Typography variant='subtitle1'>
        <ul>
          <li>
            Δημιουργία παραβόλου με βάση το ΑΦΜ και τα ελάχιστα στοιχεία του αιτούντος.
          </li>
          <li>
            Το <span style={{fontWeight: 'bold'}}>ποσό που αποστέλλεται</span> είναι είτε το ποσό που είχε επιστραφεί από την 
            κλήση για την <span style={{fontWeight: 'bold'}}>ανάκτηση των τύπων παραβόλου</span>, είτε
            <span style={{fontWeight: 'bold'}}> το μεταβλητό ποσό</span> που έχει επιλέξει ο συναλλασσόμενος
          </li>
          <li>
            Στην περίπτωση <span style={{fontWeight: 'bold'}}>φυσικού προσώπου</span> τα πεδία Όνομα, Επώνυμο και Πατρώνυμο
            συμπληρώνονται <span style={{textDecoration: 'underline', fontWeight: 'bold'}}>μόνο</span> με τον πρώτο χαρακτήρα, 
            ενώ στην περίπτωση <span style={{fontWeight: 'bold'}}>επιχείρησης</span> συμπληρώνεται <span style={{textDecoration: 'underline', fontWeight: 'bold'}}>μόνο </span> 
            το πεδίο Επώνυμο με τους <span style={{textDecoration: 'underline', fontWeight: 'bold'}}>τρεις</span> πρώτους χαρακτήρες της επωνυμίας.
          </li>
        </ul>
      </Typography>
    </FormHelperText>
    <FilterComponentV2
      title='Δημιουργία Παραβόλου με Ελάχιστα Στοιχεία'
      validationSchema={createParavoloShortValidationSchema}
      filterFields={createParavoloShortFields}
      initialValues={createParavoloShortInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitShort(filterValues)}
      validateOnBlur={false}
      mode='create'
    />
    <Box mt={5} >
      {isLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
        : error
          ? <Typography variant='h4' display='flex'
            justifyContent='center'>
            {error}
          </Typography> 
          : data
            ? <Box height='100%'>
              <Details rows={details_short}/>
            </Box> 
            : null 
      }
    </Box>
  </Box>;

  const tabContent = [
    tab1,
    tab2,
  ];

  return (
    <Box mt={5}>
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>Δημιουργία Ηλεκτρονικού Παραβόλου</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main}} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
        resetFilters={() => {
          setData(undefined);
          setError('');
        }}
      />
    </Box>
  );
};

export default CreateParavolo;